import * as React from 'react';
import {
  Quote,
  SegmentedControl,
  SidePanel,
  WorkspaceButton,
  WorkspaceNotice,
} from '@/components/ui/workspace';
import { ApiError } from '@/lib/api/client';
import type { Criticality } from '@/lib/api/engagement-requirements';
import { CHOOSABLE_CRITICALITIES, CRITICALITY_LABEL } from './requirements-logic';

/**
 * "Write one manually" side-panel form (T-0142; E11-F04 scenario 5).
 *
 * Reference: `newPanel()` in
 * `docs/requirement-library/prototype/selection.js`. Module, sub-category
 * (suggesting the module's existing ones), text, criticality, and the
 * citation: a required "Why is this being added?" and an optional
 * supporting document. Nobody types a number (amendment 01, Decision
 * 32): the server assigns the next one in the module's client series and
 * the screen shows it once the requirement is saved. The add button
 * stays disabled until both the text and the reason are filled.
 *
 * Wording the engagement already has is refused by the server (409,
 * `duplicate_text`). The form then says "Same as PAY-C01", quotes the
 * requirement it repeats, and offers "Add anyway", which sends the same
 * values with `acknowledge_duplicate` — the import's "Same as" / "Import
 * anyway" choice, for one requirement.
 */

const FIELD =
  'block w-full rounded-card border border-ink-200 bg-paper-0 px-3 text-body-sm text-ink-900 placeholder:text-ink-300 hover:border-ink-300 focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-accent-amber-600';
const LABEL = 'mb-1.5 block text-meta font-medium text-ink-700';
const REQUIRED = <span className="font-normal text-posture-critical">*</span>;

const SEGMENTS = CHOOSABLE_CRITICALITIES.map((c) => ({
  value: c,
  label: c === 'not_applicable' ? 'N/A' : CRITICALITY_LABEL[c],
}));

export interface ManualAddModule {
  id: string;
  name: string;
  prefix: string;
  /** Sub-categories already used in this module, for suggestions. */
  subcategories: ReadonlyArray<string>;
}

export interface ManualAddValues {
  module_id: string;
  subcategory: string;
  text: string;
  criticality: Criticality;
  reason: string;
  supporting_document_id?: string;
  /** Set by "Add anyway": add it although the wording is already there. */
  acknowledge_duplicate?: boolean;
}

/** The requirements a refused add repeats, and the wording that was refused. */
export interface ManualDuplicate {
  text: string;
  matches: ReadonlyArray<{ number: string; text: string; module_name: string | null }>;
}

/**
 * The duplicate a failed add reports, or null when the error is something
 * else. `text` is the wording that was sent.
 */
export function duplicateFromError(err: unknown, text: string): ManualDuplicate | null {
  if (!(err instanceof ApiError) || err.code !== 'conflict') return null;
  const details = err.details;
  if (!details || details.reason !== 'duplicate_text' || !Array.isArray(details.matches)) {
    return null;
  }
  const matches = details.matches.flatMap((m: unknown) => {
    const r = m && typeof m === 'object' ? (m as Record<string, unknown>) : null;
    if (!r || typeof r.number !== 'string' || typeof r.text !== 'string') return [];
    return [
      {
        number: r.number,
        text: r.text,
        module_name: typeof r.module_name === 'string' ? r.module_name : null,
      },
    ];
  });
  return matches.length > 0 ? { text, matches } : null;
}

export interface ManualAddPanelProps {
  modules: ReadonlyArray<ManualAddModule>;
  /** Module preselected when the form opens. */
  defaultModuleId: string | null;
  /** Documents of this engagement that can back the requirement. */
  documents: ReadonlyArray<{ id: string; name: string }>;
  saving: boolean;
  error: string | null;
  /** The last add was refused as a duplicate of these (shown while the text is unchanged). */
  duplicate?: ManualDuplicate | null;
  onSubmit: (values: ManualAddValues) => void;
  onClose: () => void;
}

export function ManualAddPanel({
  modules,
  defaultModuleId,
  documents,
  saving,
  error,
  duplicate = null,
  onSubmit,
  onClose,
}: ManualAddPanelProps) {
  const id = React.useId();
  const [moduleId, setModuleId] = React.useState(
    () => modules.find((m) => m.id === defaultModuleId)?.id ?? modules[0]?.id ?? '',
  );
  const [subcategory, setSubcategory] = React.useState('General');
  const [text, setText] = React.useState('');
  const [criticality, setCriticality] = React.useState<Criticality>('not_set');
  const [reason, setReason] = React.useState('');
  const [documentId, setDocumentId] = React.useState('');
  const textRef = React.useRef<HTMLTextAreaElement>(null);

  // The module list arrives with the library tree; pick one once it does.
  React.useEffect(() => {
    if (!moduleId && modules[0]) setModuleId(modules[0].id);
  }, [moduleId, modules]);

  React.useEffect(() => {
    textRef.current?.focus();
  }, []);

  const module = modules.find((m) => m.id === moduleId) ?? null;
  const canSave = Boolean(module) && text.trim().length > 0 && reason.trim().length > 0 && !saving;

  // The refusal speaks about the wording that was sent; edit it and it no longer applies.
  const repeated = duplicate && duplicate.text === text.trim() ? duplicate : null;

  const submit = (acknowledgeDuplicate = false) => {
    if (!canSave || !module) return;
    onSubmit({
      module_id: module.id,
      subcategory: subcategory.trim() || 'General',
      text: text.trim(),
      criticality,
      reason: reason.trim(),
      ...(documentId ? { supporting_document_id: documentId } : {}),
      ...(acknowledgeDuplicate ? { acknowledge_duplicate: true } : {}),
    });
  };

  return (
    <SidePanel
      aria-label="New requirement"
      identifier="New requirement"
      onClose={onClose}
      footer={
        <>
          <WorkspaceButton variant="tertiary" onClick={onClose}>
            Cancel
          </WorkspaceButton>
          <WorkspaceButton variant="primary" disabled={!canSave} onClick={() => submit()}>
            {saving ? 'Adding…' : 'Add requirement'}
          </WorkspaceButton>
        </>
      }
    >
      <form
        className="flex flex-col gap-[18px]"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div>
          <label htmlFor={`${id}-module`} className={LABEL}>
            Module
          </label>
          <select
            id={`${id}-module`}
            value={moduleId}
            onChange={(e) => setModuleId(e.target.value)}
            className={`${FIELD} h-9`}
          >
            {modules.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          <p className="mt-1.5 text-meta text-ink-500">
            {module
              ? `The number is assigned automatically when you add it, in the ${module.prefix}-C series for this client.`
              : 'The number is assigned automatically when you add it.'}
          </p>
        </div>

        <div>
          <label htmlFor={`${id}-sub`} className={LABEL}>
            Sub-category
          </label>
          <input
            id={`${id}-sub`}
            value={subcategory}
            onChange={(e) => setSubcategory(e.target.value)}
            list={`${id}-subs`}
            maxLength={120}
            autoComplete="off"
            className={`${FIELD} h-9`}
          />
          <datalist id={`${id}-subs`}>
            {(module?.subcategories ?? []).map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </div>

        <div>
          <label htmlFor={`${id}-text`} className={LABEL}>
            Requirement {REQUIRED}
          </label>
          <textarea
            id={`${id}-text`}
            ref={textRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="The system has the ability to…"
            maxLength={2000}
            className={`${FIELD} min-h-[120px] resize-y py-2 leading-[20px]`}
          />
          {repeated ? (
            <WorkspaceNotice tone="at-risk" className="mt-2">
              <div className="font-semibold text-ink-900">
                Same as {repeated.matches.map((m) => m.number).join(', ')}
              </div>
              <p className="mt-0.5">This engagement already has a requirement with this wording.</p>
              <ul className="mt-2 flex flex-col gap-2">
                {repeated.matches.map((m) => (
                  <li key={m.number}>
                    <div className="mb-1 text-meta text-ink-500">
                      <span className="font-mono font-medium text-ink-900">{m.number}</span>
                      {m.module_name ? ` · ${m.module_name}` : ''}
                    </div>
                    <Quote size="sm">{m.text}</Quote>
                  </li>
                ))}
              </ul>
              <WorkspaceButton className="mt-2.5" disabled={!canSave} onClick={() => submit(true)}>
                Add anyway
              </WorkspaceButton>
            </WorkspaceNotice>
          ) : null}
        </div>

        <div>
          <div className="mb-1.5 text-meta font-medium uppercase tracking-wide text-ink-500">
            Criticality
          </div>
          <SegmentedControl
            aria-label="Criticality"
            options={SEGMENTS}
            value={criticality === 'not_set' ? null : criticality}
            onChange={setCriticality}
          />
        </div>

        <div className="rounded-card border border-ink-100 bg-paper-50 px-3 py-2.5">
          <div className="mb-2 text-meta font-medium uppercase tracking-wide text-ink-500">
            Citation
          </div>
          <label htmlFor={`${id}-reason`} className={LABEL}>
            Why is this being added? {REQUIRED}
          </label>
          <textarea
            id={`${id}-reason`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Finance Director asked for this at the 18 Sep workshop; not covered by the library"
            maxLength={2000}
            className={`${FIELD} min-h-[72px] resize-y py-2 leading-[20px]`}
          />
          {documents.length > 0 ? (
            <>
              <label htmlFor={`${id}-doc`} className={`${LABEL} mt-2.5`}>
                Supporting document
              </label>
              <select
                id={`${id}-doc`}
                value={documentId}
                onChange={(e) => setDocumentId(e.target.value)}
                className={`${FIELD} h-9`}
              >
                <option value="">None</option>
                {documents.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </>
          ) : null}
          <p className="mt-1.5 text-meta text-ink-500">
            Every requirement in an engagement must be traceable to where it came from.
          </p>
        </div>

        {error ? (
          <p role="alert" className="text-meta text-posture-critical">
            {error}
          </p>
        ) : null}
      </form>
    </SidePanel>
  );
}
